<template>
    <div class="source-funnels-chart" :class="{ fullscreen: fullScreen }">
        <div class="title" v-if="fullScreen">
            <ChartDoddleIcon />
            <h2>Müşteri Takip Kırılımları</h2>
        </div>
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>
        <div class="toolbar filter-toolbar">
            <div class="left">
                <CommonFilterTool
                    v-model="breakdownFilter"
                    :keep-state-on-query-params="false"
                    :filter-meta="filterMetas"
                    :label-formatter="filterLabelFormatter"
                    @addFilter="addFilter"
                />
            </div>
        </div>

        <div class="source-buttons">
            <div class="buttons">
                <SupsisButton
                    v-for="source of getAllUtmSourceList()"
                    :key="source"
                    :classNames="selectedUtmSource ? '' : 'selected'"
                    @click="() => (selectedUtmSource = source)"
                    small
                    :ghost="source !== selectedUtmSource"
                    :primary="source === selectedUtmSource"
                >
                    {{ source }}
                </SupsisButton>
            </div>
        </div>
        <FunnelChart
            title="Müşteri Takip Kırılımları"
            :legends="legends"
            :colors="colors"
            :series="getSeries()"
            v-if="!tableMode"
        />
        <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        <div class="stats">
            <StatCard title="General Win Rate" :value="stats.ViewWinPercent" unit="%" />
            <StatCard title="Demo Form Win Rate" :value="stats.DFWinPercent" unit="%" />
            <StatCard title="Demo Form Visit Percent" :value="stats.ViewerDFViewerPercent" unit="%" />
            <StatCard title="Demo Form Submit Percent" :value="stats.DFSubmitterPercent" unit="%" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StackedAreaChart from "../../Pages/Chatbot/pages/reports/charts/StackedAreaChart.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import { expertToExcel } from "../../../utils/utils";
import { dateToHourFromSecondsShortFormat } from "../../../utils/filters";
import { ADMIN_REPORT_API, REPORT_API } from "../../../api/statistics";
import log2 from "../../../utils/log2";
import ChartDoddleIcon from "../common/icons/ChartDoddleIcon.vue";
import fscreen from "fscreen";
import FunnelChart from "../../Pages/Chatbot/pages/reports/charts/FunnelChart.vue";
import SupsisButton from "../../Pages/Settings/components/SupsisButton.vue";
import CommonFilterTool from "../../Pages/Chatbot/pages/archive/CommonFilterTool.vue";

const log = log2("SourceFunnels");
export default Vue.extend({
    name: "SourceFunnels",
    components: {
        CommonFilterTool,
        FunnelChart,
        ChartDoddleIcon,
        TableView,
        IconButton,
        StatCard,
        StackedAreaChart,
        SupsisButton,
    },
    props: {
        filters: {
            type: Object,
            default: () => ({
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
    },
    data() {
        return {
            colors: ["#FFBF00"],
            legends: ["Viewers", "DF Viewers", "DF Fillers", "DF Submitters", "SUPSIS Customers"],
            stats: {
                ViewerDFViewerPercent: 0,
                DFSubmitterPercent: 0,
                ViewWinPercent: 0,
                DFWinPercent: 0,
            },
            tableMode: false,
            loading: true,
            loadingInstance: null,
            graphData: [],
            breakdowns: {
                distinctUtmSources: [],
                distinctUtmMediums: [],
                distinctUtmCampaigns: [],
                distinctUtmContents: [],
                distinctUtmTerms: [],
            },
            fullScreen: false,
            cancels: [],
            selectedUtmSource: "total",
            filterMetas: [
                {
                    name: "utmSource",
                    label: "Source",
                    materialIcon: "filter_alt",
                    controlType: "select",
                    defaultValue: null,
                    options: () =>
                        this.breakdowns.distinctUtmSources.map((value) => ({
                            id: value,
                            label: value,
                        })),
                },
                {
                    name: "utmMedium",
                    label: "Medium",
                    materialIcon: "filter_alt",
                    controlType: "select",
                    defaultValue: null,
                    options: () =>
                        this.breakdowns.distinctUtmMediums.map((value) => ({
                            id: value,
                            label: value,
                        })),
                },
                {
                    name: "utmCampaign",
                    label: "Campaign",
                    materialIcon: "filter_alt",
                    controlType: "select",
                    defaultValue: null,
                    options: () =>
                        this.breakdowns.distinctUtmCampaigns.map((value) => ({
                            id: value,
                            label: value,
                        })),
                },
                {
                    name: "utmContent",
                    label: "Content",
                    materialIcon: "filter_alt",
                    controlType: "select",
                    defaultValue: null,
                    options: () =>
                        this.breakdowns.distinctUtmContents.map((value) => ({
                            id: value,
                            label: value,
                        })),
                },
                {
                    name: "utmTerm",
                    label: "Term",
                    materialIcon: "filter_alt",
                    controlType: "select",
                    defaultValue: null,
                    options: () =>
                        this.breakdowns.distinctUtmTerms.map((value) => ({
                            id: value,
                            label: value,
                        })),
                },
            ],
            breakdownFilter: [
                {
                    name: "utmSource",
                    label: "Source",
                    filterValue: null,
                    materialIcon: "filter_alt",
                    controlType: "select",
                    options: () =>
                        this.breakdowns.distinctUtmSources.map((value) => ({
                            id: value,
                            label: value,
                        })),
                },
            ],
        };
    },
    methods: {
        addFilter(name) {
            log.info("addFilter", name);
            const meta = this.filterMetas.find((x) => x.name === name);
            this.breakdownFilter.push({
                name: meta.name,
                label: meta.label,
                filterValue: meta.defaultValue,
                materialIcon: meta.materialIcon,
                controlType: meta.controlType,
                options: meta.options,
            });
        },
        filterLabelFormatter(name, value) {
            const filter = this.filterMetas.find((x) => x.name === name);
            log.info("filterLabelFormatter", { name, value, filter });
            if (value) {
                return `${filter.label}: ${value}`;
            }
            return filter.label;
        },
        getAllUtmSourceList() {
            const sourceList = _.chain(this.graphData || [])
                .map("_id")
                .value();
            return ["total", ...sourceList];
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".source-funnels-chart");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        generateFilterParams() {
            const filters = _.cloneDeep(this.filters);
            filters.breakdowns = this.breakdownFilter
                .map((filter) => {
                    if (!!filter.filterValue) {
                        return {
                            key: filter.name,
                            value: filter.filterValue,
                        };
                    }
                    return undefined;
                })
                .filter((x) => !!x);

            return filters;
        },
        fetch() {
            log.info("fetch");
            this.loading = true;
            this.loadingInstance.show();
            const filters = this.generateFilterParams();
            ADMIN_REPORT_API.CUSTOMER_CANDIDATE.getFunnelsBySource(filters)
                .then((res) => {
                    log("getFunnelsBySource data fetched", res);
                    this.graphData = res;
                    this.getStats();
                })
                .finally(() => {
                    this.loading = false;
                });
            ADMIN_REPORT_API.CUSTOMER_CANDIDATE.getBreakdowns({
                breakdowns: filters.breakdowns,
            }).then((res) => {
                this.breakdowns = res;
            });
        },
        humanizeDuration(totalSeconds) {
            return dateToHourFromSecondsShortFormat(totalSeconds, this.$i18n.locale, true);
            // return `${hours}h ${minutes}m ${seconds}s`;
        },
        getCols() {
            return [
                {
                    title: "Type",
                    dataIndex: "type",
                    key: "type",
                },
                {
                    title: "approved",
                    dataIndex: "approved",
                    key: "approved",
                },
                {
                    title: "filled",
                    dataIndex: "filled",
                    key: "filled",
                },
                {
                    title: "submitted",
                    dataIndex: "submitted",
                    key: "submitted",
                },
                {
                    title: "total",
                    dataIndex: "total",
                    key: "total",
                },
                {
                    title: "viewed",
                    dataIndex: "viewed",
                    key: "viewed",
                },
            ];
        },
        getRows() {
            const items = this.graphData || items;
            log("getRows.buckets getSourceFunnels...", items);
            return items;
        },
        getStats() {
            const items = this.graphData || [];

            const data = {
                total: _.chain(items || [])
                    .filter((x) => (this.selectedUtmSource === "total" ? true : x._id === this.selectedUtmSource))
                    .map("total")
                    .sum()
                    .value(),
                viewed: _.chain(items || [])
                    .filter((x) => (this.selectedUtmSource === "total" ? true : x._id === this.selectedUtmSource))
                    .map("viewed")
                    .sum()
                    .value(),
                filled: _.chain(items || [])
                    .filter((x) => (this.selectedUtmSource === "total" ? true : x._id === this.selectedUtmSource))
                    .map("filled")
                    .sum()
                    .value(),
                submitted: _.chain(items || [])
                    .filter((x) => (this.selectedUtmSource === "total" ? true : x._id === this.selectedUtmSource))
                    .map("submitted")
                    .sum()
                    .value(),
                approved: _.chain(items || [])
                    .filter((x) => (this.selectedUtmSource === "total" ? true : x._id === this.selectedUtmSource))
                    .map("approved")
                    .sum()
                    .value(),
            };
            let ViewerDFViewerPercent = data.total === 0 ? 0 : (data.viewed / data.total) * 100;
            let DFSubmitterPercent = data.viewed === 0 ? 0 : (data.filled / data.viewed) * 100;
            let ViewWinPercent = data.total === 0 ? 0 : (data.approved / data.total) * 100;
            let DFWinPercent = data.viewed === 0 ? 0 : (data.approved / data.viewed) * 100;
            ViewerDFViewerPercent = parseFloat(ViewerDFViewerPercent.toFixed(2));
            DFSubmitterPercent = parseFloat(DFSubmitterPercent.toFixed(2));
            ViewWinPercent = parseFloat(ViewWinPercent.toFixed(2));
            DFWinPercent = parseFloat(DFWinPercent.toFixed(2));
            return {
                ViewerDFViewerPercent, // toplam site ziyaretci sayısı / demo form görüntüleyenler oranını verir
                DFSubmitterPercent, // demo form görüntüleyenler / demo form doldurmayı gönderenler oranını verir
                ViewWinPercent, // genel kazanma oranı =  toplam site ziyaretci sayısı / demo hesap açanlar
                DFWinPercent, // demo form doldurmayı görenler / demo hesap açanlar
            };
        },
        getXAxisData() {
            const { buckets, timeSeriesType } = this.graphData;
            log("getXAxisData.buckets", buckets);
            return buckets.map((item) => {
                if (timeSeriesType === "day") {
                    return moment(item.date).format("DD/MM");
                } else if (timeSeriesType === "hour") {
                    return moment(item.date).format("HH:mm");
                } else if (timeSeriesType === "week") {
                    return moment(item.date).format("YYYY-WW[week]");
                } else {
                    //monthly
                    return moment(item.date).format("MMM");
                }
            });
        },
        getSeries() {
            const common = {
                name: "Kişi Sayısı (adet)",
                type: "funnel",
                left: "10%",
                width: "80%",
                maxSize: "80%",
                label: {
                    // position: "inside",
                    formatter: "{b}: {c} kişi",
                },

                itemStyle: {
                    borderColor: "#fff",
                    borderWidth: 2,
                },
                emphasis: {
                    label: {
                        formatter: "{b}: {c} kişi ",
                    },
                },
                data: [
                    { value: 0, name: this.legends[0] },
                    { value: 0, name: this.legends[1] },
                    { value: 0, name: this.legends[2] },
                    { value: 0, name: this.legends[3] },
                    { value: 0, name: this.legends[4] },
                ],
            };
            const mode = this.selectedUtmSource.toLocaleLowerCase();
            if (mode === "total") {
                const total = _.chain(this.graphData || [])
                    .map("total")
                    .sum()
                    .value();
                const viewed = _.chain(this.graphData || [])
                    .map("viewed")
                    .sum()
                    .value();
                const filled = _.chain(this.graphData || [])
                    .map("filled")
                    .sum()
                    .value();
                const submitted = _.chain(this.graphData || [])
                    .map("submitted")
                    .sum()
                    .value();
                const approved = _.chain(this.graphData || [])
                    .map("approved")
                    .sum()
                    .value();
                const series = _.cloneDeep(common);
                series.data = [
                    { value: total, name: this.legends[0] },
                    { value: viewed, name: this.legends[1] },
                    { value: filled, name: this.legends[2] },
                    { value: submitted, name: this.legends[3] },
                    { value: approved, name: this.legends[4] },
                ];
                return [series];
            } else {
                const total = _.chain(this.graphData || [])
                    .filter((x) => x._id === mode)
                    .map("total")
                    .sum()
                    .value();
                const viewed = _.chain(this.graphData || [])
                    .filter((x) => x._id === mode)
                    .map("viewed")
                    .sum()
                    .value();
                const filled = _.chain(this.graphData || [])
                    .filter((x) => x._id === mode)
                    .map("filled")
                    .sum()
                    .value();
                const submitted = _.chain(this.graphData || [])
                    .filter((x) => x._id === mode)
                    .map("submitted")
                    .sum()
                    .value();
                const approved = _.chain(this.graphData || [])
                    .filter((x) => x._id === mode)
                    .map("approved")
                    .sum()
                    .value();
                const series = _.cloneDeep(common);
                series.data = [
                    { value: total, name: this.legends[0] },
                    { value: viewed, name: this.legends[1] },
                    { value: filled, name: this.legends[2] },
                    { value: submitted, name: this.legends[3] },
                    { value: approved, name: this.legends[4] },
                ];
                return [series];
            }
            return [common];
        },
        exportToExcel() {
            const items = this.graphData || [];
            const excelData = items.map((item) => {
                return {
                    approved: item.approved,
                    filled: item.filled,
                    submitted: item.submitted,
                    total: item.total,
                    viewed: item.viewed,
                    type: item._id,
                };
            });
            expertToExcel(excelData, `source-funnels.xlsx`, "Sheet 1");
        },
    },
    created() {
        const fullscreenCallback = () => {
            log.info("fullscreenCallback", fscreen.fullscreenElement);
            this.fullScreen = fscreen.fullscreenElement !== null;
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".source-funnels-chart"),
            name: "wave",
        });
        this.stats = this.getStats();
        this.fetch();
    },
    watch: {
        tableMode() {
            console.log("tableMode watch..", this.tableMode);
        },
        selectedUtmSource: {
            handler: function (val, oldVal) {
                this.stats = this.getStats();
            },
        },
        filters: {
            handler: function (val, oldVal) {
                this.fetch();
            },
            deep: true,
        },
        breakdownFilter: {
            handler: function (val, oldVal) {
                log.info("breakdownFilter changed");
                this.fetch();
            },
            deep: true,
        },
        graphData: {
            handler: function (val, oldVal) {
                this.stats = this.getStats();
            },
            deep: true,
        },
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>
<style lang="scss">
.source-funnels-chart {
    .title {
        display: flex;
        gap: 4px;
        align-items: center;
        justify-content: center;
        margin: 0;

        h2 {
            text-align: center;
            font-weight: 700;
            font-size: 40px;
            margin: 0 !important;
        }
    }

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .stacked-area-chart {
            height: calc(100vh - 200px);
        }
    }
}
</style>
<style scoped lang="scss">
.source-funnels-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .source-buttons {
        .buttons {
            display: flex;
            align-items: center;
            justify-content: center;

            .supsis-button {
                border-radius: 0;
            }
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
